// --- Dependencies -------------------------------------------------------------------------------
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOverviewMeta, TyOverviewSupport } from "@/types";
import StatTile from "@/UI/atoms/StatTile";
import { formatDate, formatNumber, rangeLabel } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewSupport;
    meta: TyOverviewMeta;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Counts plus the queue itself. A ticket total is only useful next to the tickets it is counting, so the
 * oldest unanswered ones sit right beside it — the number is the alarm, the list is the work.
 */
export default function SupportSection({ data, meta }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const period = rangeLabel(meta.range, t);
    const count = (value: number) => formatNumber(value, lang);

    return (
        <section className="home-section">
            <div className="home-section-head">
                <h2 className="home-section-title">{t.home.supportTitle}</h2>
                <NavLink className="home-section-link" to="/support">
                    {t.home.viewAll}
                </NavLink>
            </div>

            <div className="home-tiles">
                <StatTile
                    label={t.home.ticketsOpen}
                    value={count(data.open)}
                    hint={t.home.ticketsOpenHint}
                    tone={data.open > 0 ? "bad" : "good"}
                    to="/support"
                />
                <StatTile label={t.home.ticketsPending} value={count(data.pending)} tone={data.pending > 0 ? "warn" : "neutral"} to="/support" />
                <StatTile label={t.home.ticketsContacted} value={count(data.contacted)} to="/support" />
                <StatTile label={t.home.ticketsTotal} value={count(data.total)} hint={period} to="/support" />
            </div>

            <div className="home-card">
                <p className="home-card-title">{t.home.ticketsLatest}</p>
                {data.latest.length === 0 ? (
                    <p className="home-empty">{t.home.ticketsNone}</p>
                ) : (
                    <ul className="home-list">
                        {data.latest.map((ticket) => (
                            <li key={ticket._id} className="home-list-row home-list-row--static">
                                <span className="home-list-main">
                                    <span className="home-list-name">{ticket.title}</span>
                                    <span className="home-list-sub">{ticket.store?.name ?? ticket.client?.name ?? "—"}</span>
                                </span>
                                <span className="home-list-value home-list-value--muted">{formatDate(ticket.createdAt, lang)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
